import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateLoadingState } from '@sales-center/common'
import {
  selectAllSalesFunnels,
  selectAllSalesFunnelStages,
} from '@user/store/salesFunnel/slice'
import { useBulkActionContext } from '../../../BulkActionContext'
import { normalize, validateForm } from '../validation'
import { BulkUpdateStatusFormModel, SubmitCallback } from '../typings'
import { toast } from '@sales-center/components'
import { selectAllLeads } from '@user/store/lead'
import { ENTITY_TYPES } from '@sales-center/common'
import { selectAllOpportunities } from '@user/store/opportunity'

type Params = {
  onSubmit: SubmitCallback
  entityType: ENTITY_TYPES
}

type Records = {
  id: number
  salesFunnelId: number
}

export const useFormActions = ({ onSubmit, entityType }: Params) => {
  const appDispatch = useAppDispatch()

  const { selectedRows, dispatch } = useBulkActionContext()

  const leads = useSelector(selectAllLeads)
  const opportunities = useSelector(selectAllOpportunities)
  const salesFunnels = useSelector(selectAllSalesFunnels)
  const salesFunnelStages = useSelector(selectAllSalesFunnelStages)

  const initSalesFunnel = useMemo(() => {
    const items: Records[] =
      entityType === ENTITY_TYPES.Lead ? leads : opportunities

    const selectedSalesFunnels = items
      .filter((item) => selectedRows.includes(item.id))
      .map((item) => item.salesFunnelId)

    const sameSalesFunnels =
      selectedSalesFunnels.length > 0 &&
      selectedSalesFunnels.every((id) => id === selectedSalesFunnels[0])

    return sameSalesFunnels
      ? salesFunnels.find(
          (funnel) => Number(funnel.id) === selectedSalesFunnels[0],
        )
      : undefined
  }, [entityType, leads, selectedRows, opportunities, salesFunnels])

  const validate = useCallback(
    (values: BulkUpdateStatusFormModel) =>
      validateForm(values, salesFunnelStages),
    [salesFunnelStages],
  )

  const submitHandler = useCallback(
    async (values: BulkUpdateStatusFormModel) => {
      try {
        appDispatch(updateLoadingState('visible'))
        await onSubmit(normalize(selectedRows, values))
        dispatch({ type: 'setActive' })
      } finally {
        appDispatch(updateLoadingState('hidden'))
        toast.success('Bulk update successfully applied')
      }
    },
    [appDispatch, dispatch, onSubmit, selectedRows],
  )

  return {
    salesFunnels,
    salesFunnelStages,
    validate,
    submitHandler,
    initSalesFunnel,
  }
}
